<script setup>
/* eslint-disable */
import { onMounted, nextTick, ref, computed } from "vue";
import { useStore } from "vuex";
import { loadScript } from "@paypal/paypal-js";
import myApi from "@/plugins/axios";

const store = useStore();
const isAuthenticated = computed(() => !!store.getters.loggedIn);

const rawProps = defineProps({
  clientGuest: { type: Object, required: false, default: undefined },
  deliveryInformation: { type: Object, required: false, default: undefined },
  orderMeta: { type: Object, required: false, default: undefined },

  client_guest: { type: Object, required: false, default: undefined },
  delivery_information: { type: Object, required: false, default: undefined },
  type_entrega: { type: [Number, String], required: false, default: undefined },
  sucursal: { type: String, required: false, default: undefined },

  is_credito_fiscal: {
    type: [Boolean, String, Number],
    required: false,
    default: undefined,
  },
});

const pClient = computed(
  () => rawProps.clientGuest ?? rawProps.client_guest ?? {}
);
const pDelivery = computed(
  () => rawProps.deliveryInformation ?? rawProps.delivery_information ?? {}
);

const pOrderMeta = computed(() => {
  const te =
    rawProps.type_entrega != null
      ? Number(rawProps.type_entrega)
      : rawProps.orderMeta?.type_entrega != null
      ? Number(rawProps.orderMeta.type_entrega)
      : undefined;

  const suc =
    (pDelivery.value?.sucursal ??
      rawProps.orderMeta?.sucursal ??
      rawProps.sucursal) ||
    undefined;

  return {
    ...(rawProps.orderMeta || {}),
    type_entrega: te,
    sucursal: suc,
    is_credito_fiscal:
      rawProps.orderMeta?.is_credito_fiscal ?? rawProps.is_credito_fiscal,
  };
});

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Number(n.toFixed(2)) : 0;
}

function readGuestUUID() {
  const m = document.cookie.match(/(?:^|;\s*)gcid=([^;]+)/);
  if (m?.[1]) return decodeURIComponent(m[1]);
  const ls = localStorage.getItem("guest_uuid");
  if (ls) return ls;
  const g = crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
  try {
    localStorage.setItem("guest_uuid", g);
  } catch {}
  return g;
}

function readGuestItems() {
  try {
    const raw = localStorage.getItem("carts");
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr)
      ? arr
          .filter((i) => i && i.uniqd)
          .map((i) => ({
            uniqd: String(i.uniqd),
            quantity: Number(i.quantity || 1),
          }))
      : [];
  } catch {
    return [];
  }
}

function shippingFromProps() {
  const di = pDelivery.value || {};
  const om = pOrderMeta.value || {};
  const c = [
    om?.shipping,
    om?.envio,
    om?.envio_price,
    om?.delivery_price,
    om?.delivery_cost,
    di?.shipping,
    di?.price,
    di?.envio,
    di?.envio_price,
    0,
  ];
  const pick = c.find(
    (v) => v !== undefined && v !== null && !isNaN(Number(v))
  );
  return num(pick);
}

function asBool(v) {
  if (v === true || v === false) return v;
  if (v == null) return null;
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "sí", "si", "yes", "on"].includes(s)) return true;
  if (["0", "false", "no", "off", ""].includes(s)) return false;
  return null;
}

function readSucursal() {
  const s =
    pDelivery.value?.sucursal ??
    pOrderMeta.value?.sucursal ??
    rawProps.sucursal ??
    null;
  return s && String(s).trim() ? String(s).trim() : null;
}

function currentTypeEntrega() {
  const te = Number(pOrderMeta.value?.type_entrega);
  return te === 2 ? 2 : 1;
}

function buildDeliveryInformation(te) {
  const di = pDelivery.value || {};
  const cg = pClient.value || {};

  if (te === 1) {
    return {
      sucursal: readSucursal(),
      departament: null,
      municipality: null,
      address: null,
      point_reference: null,
    };
  }
  return {
    sucursal: null,
    departament:
      di.departament ??
      di.department ??
      di.departamento ??
      cg.departament ??
      cg.department ??
      cg.departamento ??
      null,
    municipality:
      di.municipality ??
      di.municipio ??
      cg.municipality ??
      cg.municipio ??
      null,
    address: di.address ?? cg.address ?? null,
    point_reference: di.point_reference ?? cg.point_reference ?? null,
  };
}

function resolvedShipping(te) {
  return te === 2 ? shippingFromProps() : 0;
}

const emit = defineEmits(["success", "error"]);
const paypalContainer = ref(null);
let paypal, buttons;
const lastOrderID = ref(null);

async function mountButtons() {
  if (!paypal) {
    paypal = await loadScript({
      "client-id": process.env.VUE_APP_PAYPAL_CLIENT,
      currency: "USD",
      disableFunding: "card",
    });
    if (!paypal?.Buttons) throw new Error("SDK de PayPal no disponible");
  }
  if (buttons) return;

  buttons = paypal.Buttons({
    createOrder: async () => {
      try {
        const te = currentTypeEntrega();
        const shipping = resolvedShipping(te);
        const diPayload = buildDeliveryInformation(te);
        const sucursalRoot = readSucursal();

        const logged = isAuthenticated.value;
        const guestUUID = !logged ? readGuestUUID() : null;
        const items = !logged ? readGuestItems() : [];
        const base = {
          ...(logged ? {} : { as_guest: true, items }),
          shipping,
          type_entrega: te,
          client_guest: pClient.value,
          ...(te === 2
            ? { client_guest: pClient.value, delivery_information: diPayload }
            : {}),
          ...(te === 1 ? { sucursal: sucursalRoot ?? null } : {}),
          client_guest: pClient.value,
          ...(te === 2
            ? { delivery_information: diPayload }
            : { sucursal: sucursalRoot ?? null }),
        };

        console.debug("[PP][btn] createOrder", {
          te,
          shipping,
          sucursalRoot,
          diPayload,
          is_credito_fiscal_dbg: asBool(
            pOrderMeta.value?.is_credito_fiscal ?? rawProps.is_credito_fiscal
          ),
          base,
        });

        const headers = logged ? undefined : { "X-Guest-UUID": guestUUID };
        const { data } = await myApi.post(
          "/payments/paypal/create-order",
          base,
          { headers }
        );
        lastOrderID.value = data?.orderID;
        return data.orderID;
      } catch (err) {
        emit("error", err);
        throw err;
      }
    },

    onApprove: async ({ orderID }) => {
      try {
        const te = currentTypeEntrega();
        const shipping = resolvedShipping(te);
        const diPayload = buildDeliveryInformation(te);
        const sucursalRoot = readSucursal();

        const payload = {
          orderID,
          client_guest: pClient.value,
          delivery_information: diPayload,
          is_credito_fiscal: asBool(
            pOrderMeta.value?.is_credito_fiscal ?? rawProps.is_credito_fiscal
          ),
          type_entrega: te,
          sucursal: sucursalRoot ?? null,
          shipping,
        };

        console.debug("[PP][btn] capture payload", payload);

        const logged = isAuthenticated.value;
        const headers = logged
          ? undefined
          : { "X-Guest-UUID": readGuestUUID() };
        const { data } = await myApi.post("/payments/paypal/capture", payload, {
          headers,
        });

        const normalized = {
          ok: !!(data?.ok ?? true),
          order_id: data?.order ?? data?.order_id ?? null,
          code: data?.code ?? data?.order_code ?? null,
          ext_id: data?.ext_id ?? data?.captureId ?? null,
          fee: data?.fee ?? data?.handling ?? null,
          uniqd: data?.uniqd ?? data?.uniqd_order ?? data?.order?.uniqd ?? null,
          _raw: data,
        };

        emit("success", normalized);
      } catch (err) {
        emit("error", err);
      }
    },

    onCancel: async () => {
      try {
        if (lastOrderID.value) {
          await myApi.post("/payments/paypal/cancel", {
            orderID: lastOrderID.value,
          });
        }
      } catch {}
    },

    onError: (err) => emit("error", err),
    style: { layout: "vertical", label: "paypal", color: "blue" },
  });

  await buttons.render(paypalContainer.value);
}

onMounted(async () => {
  await nextTick();
  await mountButtons();
});
</script>

<template>
  <div ref="paypalContainer" style="min-height: 44px"></div>
</template>
